/**
 * Message from an HTTP error body.
 * Requests that use `responseType: 'text'` leave `error` as a raw JSON string,
 * so `error.message` is missing and Angular's "Http failure response" text shows instead.
 */
export interface HttpErrorNotice {
  title: string;
  message: string;
}

export function readHttpErrorMessage(err: unknown, fallback: string): string {
  return readHttpErrorNotice(err, fallback).message;
}

/**
 * Text from the API error body, including provider messages the user-facing
 * notice hides. Angular's "Http failure response for {url}" wrapper is omitted.
 */
export function readHttpErrorDetails(err: unknown): string | null {
  const error = err as { error?: unknown } | null | undefined;
  const parts = messagesFromBody(error?.error).filter((part) => !isHiddenErrorText(part));
  if (parts.length) return replaceTechnicalNames(parts.join('\n'));

  if (typeof error?.error === 'string') {
    const raw = error.error.trim();
    if (raw && !isHiddenErrorText(raw)) return replaceTechnicalNames(raw);
  }
  return null;
}

/** Toast copy for an HTTP error, with API field names rewritten for people using the app. */
export function readHttpErrorNotice(err: unknown, fallback: string): HttpErrorNotice {
  const error = err as { error?: unknown; message?: string } | null | undefined;
  const fromBody = messageFromBody(error?.error);
  if (fromBody && !isTechnicalMessage(fromBody)) return toUserNotice(fromBody);

  const httpMessage = error?.message?.trim();
  if (httpMessage && !isTechnicalMessage(httpMessage)) return toUserNotice(httpMessage);
  return { title: 'Something went wrong', message: fallback };
}

function messageFromBody(body: unknown): string | null {
  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (!trimmed) return null;
    try {
      const parsed = messageFromBody(JSON.parse(trimmed) as unknown);
      return parsed ?? trimmed;
    } catch {
      return trimmed;
    }
  }

  if (!body || typeof body !== 'object') return null;

  const record = body as Record<string, unknown>;
  for (const key of ['message', 'Message', 'detail', 'Detail', 'title', 'Title']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function messagesFromBody(body: unknown, seen = new Set<string>()): string[] {
  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (!trimmed) return [];
    try {
      return messagesFromBody(JSON.parse(trimmed) as unknown, seen);
    } catch {
      return pushUnique(seen, trimmed);
    }
  }

  if (!body || typeof body !== 'object') return [];

  if (Array.isArray(body)) {
    return body.flatMap((item) => messagesFromBody(item, seen));
  }

  const record = body as Record<string, unknown>;
  const out: string[] = [];
  const textKeys = [
    'message',
    'Message',
    'detail',
    'Detail',
    'details',
    'Details',
    'title',
    'Title',
    'error',
    'Error'
  ];
  for (const key of textKeys) {
    const value = record[key];
    if (typeof value === 'string') out.push(...pushUnique(seen, value.trim()));
    else out.push(...messagesFromBody(value, seen));
  }

  const errors = record['errors'] ?? record['Errors'];
  if (errors && typeof errors === 'object') out.push(...messagesFromErrorMap(errors, seen));
  return out;
}

function messagesFromErrorMap(errors: object, seen: Set<string>): string[] {
  if (Array.isArray(errors)) return errors.flatMap((item) => messagesFromBody(item, seen));

  const out: string[] = [];
  for (const value of Object.values(errors)) {
    if (typeof value === 'string') out.push(...pushUnique(seen, value.trim()));
    else if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string') out.push(...pushUnique(seen, item.trim()));
        else out.push(...messagesFromBody(item, seen));
      }
    } else {
      out.push(...messagesFromBody(value, seen));
    }
  }
  return out;
}

function pushUnique(seen: Set<string>, value: string): string[] {
  const text = value.trim();
  if (!text || seen.has(text)) return [];
  seen.add(text);
  return [text];
}

function isHiddenErrorText(message: string): boolean {
  const text = message.trim();
  if (!text || text.startsWith('Http failure response')) return true;
  return /^https?:\/\/\S+$/i.test(text);
}

function toUserNotice(message: string): HttpErrorNotice {
  const text = message.trim().replace(/\s+/g, ' ');
  if (/at least one facility\s*ids?\s+(is|are) required/i.test(text)) {
    return {
      title: 'Select a site',
      message: 'Choose a site, then try again.'
    };
  }

  const remainingAttempts = text.match(/remaining attempts?:\s*(\d+)/i);
  if (/invalid (username|user name|email|password|credentials)/i.test(text) || remainingAttempts) {
    const attemptsLeft = remainingAttempts?.[1];
    return {
      title: "Couldn't sign in",
      message: attemptsLeft
        ? `Check your username and password, then try again. You have ${attemptsLeft} attempt${attemptsLeft === '1' ? '' : 's'} left.`
        : 'Check your username and password, then try again.'
    };
  }

  if (/account (is )?locked|too many (failed )?attempts|temporarily locked/i.test(text)) {
    return {
      title: 'Account locked',
      message: 'Too many failed sign-in attempts. Please wait a bit, then try again or reset your password.'
    };
  }

  return { title: 'Something went wrong', message: replaceTechnicalNames(text) };
}

function replaceTechnicalNames(text: string): string {
  return text
    .replace(/\bfacilityIds\b/g, 'sites')
    .replace(/\bfacilityId\b/g, 'site');
}

function isTechnicalMessage(message: string): boolean {
  const text = message.trim();
  if (!text || text.startsWith('Http failure response')) return true;
  if (/https?:\/\//i.test(text)) return true;
  if (/\bzoho\b/i.test(text)) return true;
  if (/\bapi error\b/i.test(text)) return true;
  return false;
}
